import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import WorkbenchTopBar from "./WorkbenchTopBar";
import WorkbenchPane from "./WorkbenchPane";
import WorkbenchStatusBar from "./WorkbenchStatusBar";
import { useWorkbench } from "./WorkbenchContext";
import "./WorkbenchShell.css";

/**
 * The persistent application shell: a 46px top bar, three independently
 * scrolling panes, and a 44px status strip.
 *
 * The shell is pure layout and chrome. It renders whatever the route hands it
 * and knows nothing about estimating, cost math, or rate cards.
 *
 *   rail      250px   the collection (jobs, line items, cards)
 *   center    fluid   the selected thing's editor
 *   inspector 400px   where the number came from
 *
 * A route whose rail rows carry long names (the Rate Card Builder, whose rows
 * are full product-template names) can opt into a 330px rail with `wideRail`.
 * Every other screen keeps 250px.
 *
 * Below 1180px the inspector leaves the grid and becomes an overlay drawer;
 * below 820px the rail does too. Both drawers close on Escape and on a
 * backdrop click. Alt+T toggles the inspector at any width.
 */

const INSPECTOR_OVERLAY_QUERY = "(max-width: 1180px)";
const RAIL_OVERLAY_QUERY = "(max-width: 820px)";

/** matchMedia can be absent or stubbed out — never assume it returns a list. */
function safeMatchMedia(query) {
  if (
    typeof window === "undefined" ||
    typeof window.matchMedia !== "function"
  ) {
    return null;
  }
  try {
    return window.matchMedia(query) || null;
  } catch (error) {
    return null;
  }
}

function readMatch(query) {
  return Boolean(safeMatchMedia(query)?.matches);
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => readMatch(query));

  useEffect(() => {
    const mql = safeMatchMedia(query);
    if (!mql) return undefined;
    const onChange = (event) => setMatches(Boolean(event.matches));
    setMatches(Boolean(mql.matches));
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    if (typeof mql.addListener === "function") {
      mql.addListener(onChange);
      return () => mql.removeListener(onChange);
    }
    return undefined;
  }, [query]);

  return matches;
}

function WorkbenchShell({
  variant = "estimate",
  wideRail = false,
  title = "",
  subtitle = null,
  notice = null,
  meta = null,
  actions = null,
  rail = null,
  railHeader = null,
  center,
  centerHeader = null,
  inspector = null,
  inspectorHeader = null,
  inspectorOpen: inspectorOpenProp,
  onToggleInspector: onToggleInspectorProp,
  inspectorToggleLabel = "Cost trace",
  statusBar = null,
}) {
  const {
    inspectorOpen: sharedInspectorOpen,
    toggleInspector: toggleSharedInspector,
    railCollapsed,
    setRailCollapsed,
    registerShell,
  } = useWorkbench();

  // A route may own its inspector's open state (and its own persistence) by
  // passing the pair; otherwise the shell uses the shared, cross-route flag.
  const inspectorIsControlled = typeof inspectorOpenProp === "boolean";
  const inspectorOpen = inspectorIsControlled
    ? inspectorOpenProp
    : sharedInspectorOpen;
  const toggleInspector = inspectorIsControlled
    ? onToggleInspectorProp
    : toggleSharedInspector;

  const inspectorIsOverlay = useMediaQuery(INSPECTOR_OVERLAY_QUERY);
  const railIsOverlay = useMediaQuery(RAIL_OVERLAY_QUERY);

  // Drawer state is deliberately local and transient: a narrow window should
  // open with both drawers shut, without overwriting the docked preference the
  // user set at a wide width.
  const [inspectorDrawerOpen, setInspectorDrawerOpen] = useState(false);
  const [railDrawerOpen, setRailDrawerOpen] = useState(false);

  // Let the app Layout know this route brings its own chrome.
  useLayoutEffect(() => registerShell(), [registerShell]);

  const hasRail = Boolean(rail);
  const hasInspector = Boolean(inspector);

  const railDocked = hasRail && !railIsOverlay && !railCollapsed;
  const inspectorDocked = hasInspector && !inspectorIsOverlay && inspectorOpen;
  const railDrawerVisible = hasRail && railIsOverlay && railDrawerOpen;
  const inspectorDrawerVisible =
    hasInspector && inspectorIsOverlay && inspectorDrawerOpen;

  const handleToggleInspector = useCallback(() => {
    if (inspectorIsOverlay) {
      setInspectorDrawerOpen((open) => !open);
      return;
    }
    toggleInspector();
  }, [inspectorIsOverlay, toggleInspector]);

  const handleToggleRail = useCallback(() => {
    if (railIsOverlay) {
      setRailDrawerOpen((open) => !open);
      return;
    }
    setRailCollapsed(!railCollapsed);
  }, [railIsOverlay, railCollapsed, setRailCollapsed]);

  // Leaving overlay width closes whatever drawer was open so the docked
  // layout is never covered by a stray sheet.
  useEffect(() => {
    if (!inspectorIsOverlay) setInspectorDrawerOpen(false);
  }, [inspectorIsOverlay]);

  useEffect(() => {
    if (!railIsOverlay) setRailDrawerOpen(false);
  }, [railIsOverlay]);

  // Alt+T toggles the cost trace. Recalculate (Cmd/Ctrl+Enter) is owned by the
  // estimate screen, not by the shell.
  useEffect(() => {
    if (!hasInspector) return undefined;
    const onKeyDown = (event) => {
      if (!event.altKey || event.ctrlKey || event.metaKey) return;
      const isT =
        event.code === "KeyT" ||
        (typeof event.key === "string" && event.key.toLowerCase() === "t");
      if (!isT) return;
      event.preventDefault();
      handleToggleInspector();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [hasInspector, handleToggleInspector]);

  // Escape closes an open drawer.
  useEffect(() => {
    if (!railDrawerVisible && !inspectorDrawerVisible) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      setRailDrawerOpen(false);
      setInspectorDrawerOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [railDrawerVisible, inspectorDrawerVisible]);

  const closeDrawers = useCallback(() => {
    setRailDrawerOpen(false);
    setInspectorDrawerOpen(false);
  }, []);

  const railPane = hasRail ? (
    <WorkbenchPane role="rail" header={railHeader}>
      {rail}
    </WorkbenchPane>
  ) : null;

  const inspectorPane = hasInspector ? (
    <WorkbenchPane role="inspector" header={inspectorHeader}>
      {inspector}
    </WorkbenchPane>
  ) : null;

  return (
    <div
      className={`wb-shell${wideRail ? " wb-shell--wide-rail" : ""}`}
      data-variant={variant}
      data-statusbar={statusBar ? "on" : "off"}
    >
      <WorkbenchTopBar
        variant={variant}
        title={title}
        subtitle={subtitle}
        meta={meta}
        actions={actions}
        showRailToggle={hasRail}
        railOpen={railDocked || railDrawerVisible}
        onToggleRail={handleToggleRail}
        showInspectorToggle={hasInspector}
        inspectorOpen={inspectorDocked || inspectorDrawerVisible}
        onToggleInspector={handleToggleInspector}
        inspectorToggleLabel={inspectorToggleLabel}
      />

      {/* The full-width carrier for anything that must be legible at EVERY
          width. The top bar squeezes (the crumb is a min-width:0 flex child
          and its subtitle is hidden on narrow screens), so a warning about
          what is on screen cannot live up there. Its grid row is `auto` and
          collapses to nothing when no route supplies a notice. */}
      {notice}

      <div
        className="wb-panes"
        data-rail={railDocked ? "on" : "off"}
        data-inspector={inspectorDocked ? "on" : "off"}
      >
        {railDocked ? railPane : null}

        <WorkbenchPane role="center" header={centerHeader}>
          {center}
        </WorkbenchPane>

        {inspectorDocked ? inspectorPane : null}
      </div>

      {railDrawerVisible || inspectorDrawerVisible ? (
        <div
          className="wb-backdrop"
          onClick={closeDrawers}
          role="presentation"
        />
      ) : null}

      {railDrawerVisible ? (
        <div className="wb-drawer wb-drawer--rail">{railPane}</div>
      ) : null}

      {inspectorDrawerVisible ? (
        <div className="wb-drawer wb-drawer--inspector">{inspectorPane}</div>
      ) : null}

      <WorkbenchStatusBar>{statusBar}</WorkbenchStatusBar>
    </div>
  );
}

export default WorkbenchShell;
